/// <amd-module name="Core/Medius.Core.Web/Scripts/components/sendEmailDialog/emailAddressMultiSelectInput"/>
import * as React from "react";
import { Contact, Recipient } from "./types";
import { MultiSelect, MultiSelectChangeEvent, MultiSelectFilterChangeEvent, TagData } from "@progress/kendo-react-dropdowns";
import { FlexContent, FlexLayout, IconCommunicationRequiredRegular, IconStatusWarningRegular, TokenCssVariables } from "@medius/ui-controls";
import { translate, getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { isNullOrEmpty, validateEmail, validateEmails } from "./utils";
import { ErrorItem, PaddingSize } from "./errorItem";
import { filterBy } from "@progress/kendo-data-query";

export interface EmailAddressMultiSelectInputProps {
    inputLabel: string;
    isRequired?: boolean;
    addressBook: Contact[];
    recipients: Recipient[];
    onChange: (event: MultiSelectChangeEvent) => void;
    className?: string;
}

export const EmailAddressMultiSelectInput = (props: EmailAddressMultiSelectInputProps) => {
    const { inputLabel, isRequired = false, addressBook, recipients, onChange} = props;
    const [data, setData] = React.useState(addressBook);

    const onFilterChange = (event: MultiSelectFilterChangeEvent) => {
        setData(filterBy(addressBook.slice(), event.filter));
    };

    const emailChipContainerStyles = { height: "16x", display: "flex", alignItems: "center" };
    const chipContainerCommonStyles = { border: "none", height: "28px" };

    const renderRedEmailAddress = (recipient: Recipient, li:React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        return React.cloneElement(
        li,
        {
            ...li.props,
            style: {
                ...li.props.style,
                ...chipContainerCommonStyles,
                backgroundColor: TokenCssVariables.ColorErrorBackground,
            },
        },
        [
            <div key={`item-${recipient.emailAddress}`} style={emailChipContainerStyles}>
                <IconStatusWarningRegular key={`icon-${recipient.emailAddress}`} style={{paddingRight: "4px"}} />
                <span key={`span-${recipient.emailAddress}`}>{recipient.displayName}</span>
            </div>,
            li.props.children
        ] as any[]);
    };

    const renderGreenEmailAddress = (recipient: Recipient, li:React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        return React.cloneElement(
        li,
        {
            ...li.props,
            style: {
                ...li.props.style,
                ...chipContainerCommonStyles,
                backgroundColor: TokenCssVariables.ColorTeal20,
            },
        },
        [
            <div key={`item-${recipient.emailAddress}`} style={emailChipContainerStyles}>
                <span key={`span-${recipient.emailAddress}`}>{recipient.displayName}</span>
            </div>,
            li.props.children
        ] as any[]);
    };

    const renderEmailAdress = (recipient: Recipient, li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>) => {
        if(recipient.deliveryFailed || !validateEmail(recipient.emailAddress))
            return renderRedEmailAddress(recipient, li);

        return renderGreenEmailAddress(recipient, li);
    };

    const tagRender = (
        tagData: TagData,
        li: React.ReactElement<HTMLLIElement, string | React.JSXElementConstructor<any>>
    ) => renderEmailAdress(tagData.data[0] as Recipient, li);

    return (
        <FlexLayout spacerMode="on" spacerSize="small" width="fill">
            <FlexContent>
                <fieldset>
                    <div>{inputLabel}: {isRequired && isNullOrEmpty(recipients) && <IconCommunicationRequiredRegular size="vsmall" color="error" aria-hidden={true} />}</div>
                    <MultiSelect
                        filterable={true}
                        onFilterChange={onFilterChange}
                        data={data}
                        onChange={onChange}
                        value={recipients}
                        size="large"
                        allowCustom={true}
                        textField="displayName"
                        dataItemKey="emailAddress"
                        tagRender={tagRender}
                        className={props.className ? props.className : "email-address-multi-select"}
                    />
                    <style>
                        {`
                        .k-chip-action.k-chip-remove-action > span > svg > path {
                            d: path("M9.85403 9.14653C9.90048 9.19298 9.93733 9.24813 9.96247 9.30883C9.98762 9.36953 10.0006 9.43458 10.0006 9.50028C10.0006 9.56598 9.98762 9.63103 9.96247 9.69173C9.93733 9.75242 9.90048 9.80757 9.85403 9.85403C9.80757 9.90048 9.75242 9.93733 9.69173 9.96247C9.63103 9.98762 9.56598 10.0006 9.50028 10.0006C9.43458 10.0006 9.36953 9.98762 9.30883 9.96247C9.24813 9.93733 9.19298 9.90048 9.14653 9.85403L5.00028 5.70715L0.854028 9.85403C0.760208 9.94785 0.63296 10.0006 0.500278 10.0006C0.367596 10.0006 0.240348 9.94785 0.146528 9.85403C0.0527077 9.76021 2.61548e-09 9.63296 0 9.50028C-2.61548e-09 9.3676 0.0527077 9.24035 0.146528 9.14653L4.2934 5.00028L0.146528 0.854028C0.0527077 0.760208 0 0.63296 0 0.500278C0 0.367596 0.0527077 0.240348 0.146528 0.146528C0.240348 0.0527077 0.367596 0 0.500278 0C0.63296 0 0.760208 0.0527077 0.854028 0.146528L5.00028 4.2934L9.14653 0.146528C9.24035 0.0527077 9.3676 -2.61548e-09 9.50028 0C9.63296 2.61548e-09 9.76021 0.0527077 9.85403 0.146528C9.94785 0.240348 10.0006 0.367596 10.0006 0.500278C10.0006 0.63296 9.94785 0.760208 9.85403 0.854028L5.70715 5.00028L9.85403 9.14653Z");
                            transform: scale(50);
                            fill: ${TokenCssVariables.ColorIconActive};
                        }
                        .k-chip-action.k-chip-remove-action > span > svg {
                            height: 10px;
                            width: 10px;
                        }
                        `}
                    </style>
                    {isRequired && isNullOrEmpty(recipients) && <ErrorItem errorMessage={getLabelTranslation("#Core/formFieldRequiredErrorMessage", "Recipients field")} />}
                    {!validateEmails(recipients) && <ErrorItem errorMessage={translate("#Core/defaultValidation_EMAIL_INPUT_INVALID_VALUE")} paddingSize={PaddingSize.small}/>}
                </fieldset>
            </FlexContent>
        </FlexLayout>
    );
};